import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Layout, Type, Image as ImageIcon, Save, Globe, 
  Cpu, ShieldAlert, Activity, Layers, Edit2, Trash2, Star, UploadCloud, Loader2, User, Box,ShieldCheck,
} from 'lucide-react';
import MatrixFeed from './MatrixFeed';
import FeatureSection from './FeatureSection';
import ProductShowcase from './ProductShowcase';
import PricingSection from './PricingSection';
import LegalPagesManager from './LegalPagesManager';
import CookieConsent from './CookieConsent';
import SiteFooter from './SiteFooter'; 
import BlogManager from './BlogManager';
import { usePermission } from '../../hooks/usePermission';
import PublicBlogPage from './PublicBlogPage';
import BlogPost from './BlogPost';
import CmsPageView from './CmsPageView';
import DocsManager from './DocsManager';

// ... existing imports
import PublicDocsPage from './PublicDocsPage';
import PublicDownloadPage from './PublicDownloadPage';
import ChangelogPage from './ChangelogPage';
import ContactPage from './ContactPage';

// --- Image Uploader Component ---
const ImageUploader = ({ label, value, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axiosClient.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const imageUrl = res?.url || res?.data?.url;

      if (!imageUrl) {
        throw new Error("Upload succeeded but no URL returned");
      }

      onChange(imageUrl);
    } catch (err) {
      alert("Upload failed. Check console/network.");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
        {label}
      </label>

      <div className="flex gap-4 items-center">
        <div className="w-16 h-16 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative">
          {value ? (
            <img
              src={value}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <ImageIcon className="w-6 h-6 text-slate-400" />
          )}
        </div>

        <div className="flex-1 space-y-2">
          <div className="relative">
            <input
              type="file"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={handleFileChange}
              accept="image/*"
            />
            <button
              type="button"
              className="w-full bg-white border border-slate-300 text-slate-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center justify-center"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <UploadCloud className="w-4 h-4 mr-2" />
              )}
              {uploading ? "Uploading..." : "Upload New Image"}
            </button>
          </div>

          <input
            className="w-full p-2 border rounded-lg text-xs text-slate-500 bg-slate-50 font-mono"
            placeholder="or paste image URL..."
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const CmsPage = () => {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('settings'); 
  const [editingFeature, setEditingFeature] = useState(null); 
  // Preview Navigation State
  const [previewPage, setPreviewPage] = useState('home'); // 'home' | 'product' | 'pricing'
   
  // Settings State
  const [settings, setSettings] = useState({
    site_title: '', site_logo: '', site_favicon: '', 
    seo_keywords: '', hero_headline: '', hero_subtext: '',
    matrix_mode: 'hybrid' 
  });

  // Feature Form State
  const [featureForm, setFeatureForm] = useState({
     title: '', subtitle: '', description: '', icon_name: 'Shield', image_url: '', display_order: 0
  });
  
  // --- QUERIES ---
  
  // 1. Fetch CMS Settings
  const { data: fetchedSettings } = useQuery({
    queryKey: ['cmsSettings'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/cms/settings');
      return res || {};
    }
  });

  useEffect(() => {
    if (fetchedSettings) {
        setSettings(prev => ({
            ...prev,
            ...fetchedSettings, // Overwrite with DB values
            // Ensure no nulls
            hero_headline: fetchedSettings.hero_headline || '',
            hero_subtext: fetchedSettings.hero_subtext || '',
            site_title: fetchedSettings.site_title || '',
            site_logo: fetchedSettings.site_logo || '',
            site_favicon: fetchedSettings.site_favicon || '',
            matrix_mode: fetchedSettings.matrix_mode || 'hybrid',
        }));
    }
  }, [fetchedSettings]);


  const { data: publicStats } = useQuery({
    queryKey: ['publicStats'],
    queryFn: () => axiosClient.get('/public/stats'),
    refetchInterval: 5000
  });

  const { data: publicPackages = [] } = useQuery({
    queryKey: ['publicPricing'],
    queryFn: () => axiosClient.get('/public/pricing')
   });


  const { data: features = [] } = useQuery({
    queryKey: ['cmsFeatures'],
    queryFn: () => axiosClient.get('/admin/cms/features')
  });

  // Fetch Product Sections
  const { data: productSections = [] } = useQuery({ 
      queryKey: ['productSections'], 
      queryFn: () => axiosClient.get('/admin/cms/product-sections') 
   });

  // --- MUTATIONS ---
  const saveSettingsMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/cms/settings', data),
    onSuccess: () => {
        alert("Settings Saved! 🚀");
        queryClient.invalidateQueries(['cmsSettings']); 
    },
    onError: (err) => alert("Save failed: " + err.message)
  });

  const saveFeatureMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/cms/features', data),
    onSuccess: () => {
        queryClient.invalidateQueries(['cmsFeatures']);
        setEditingFeature(null);
        setFeatureForm({ title: '', subtitle: '', description: '', icon_name: 'Shield', image_url: '', display_order: 0 });
    }
  });

  const deleteFeatureMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/cms/features/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['cmsFeatures'])
  });

  // Update Product Section
  const updateProductMutation = useMutation({
    mutationFn: (data) => axiosClient.put(`/admin/cms/product-sections/${data.id}`, data),
    onSuccess: () => { queryClient.invalidateQueries(['productSections']); alert("Section Updated!"); }
  });

  const handleEditFeature = (f) => {
      setFeatureForm(f);
      setEditingFeature(f.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatLargeNumber = (num) => {
   if (!num) return "0";

   if (num >= 1_000_000_000_000) {
      return (num / 1_000_000_000_000).toFixed(2) + "T+";
   }

   if (num >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(2) + "B+";
   }

   if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(2) + "M+";
   }

   if (num >= 1_000) {
      return (num / 1_000).toFixed(2) + "K+";
   }

   return num.toString();
   };
   
  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* HEADER with GLOBAL SAVE */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm top-0 z-40">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center">
            <Layout className="w-6 h-6 text-teal-600 mr-2" />
            Website Builder
          </h1>
          <p className="text-xs text-slate-500">Changes apply immediately.</p>
        </div>
        <div className="flex gap-2">
           <div className="bg-slate-100 p-1 rounded-lg flex text-sm font-medium">
          {can('cms:manage') && (
            <>  
             <button onClick={() => setActiveTab('settings')} className={`px-4 py-2 rounded-md transition-colors ${activeTab==='settings' ? 'bg-white shadow text-teal-600' : 'text-slate-500'}`}>Settings</button>
             <button onClick={() => setActiveTab('features')} className={`px-4 py-2 rounded-md transition-colors ${activeTab==='features' ? 'bg-white shadow text-purple-600' : 'text-slate-500'}`}>Features</button>
             <button onClick={() => setActiveTab('products')} className={`px-4 py-2 rounded-md ${activeTab==='products' ? 'bg-white shadow text-emerald-600' : 'text-slate-500'}`}>Product Page</button>
             <button onClick={() => setActiveTab('pages')} className={`px-4 py-2 rounded-md ${activeTab==='pages' ? 'bg-white shadow text-emerald-600' : 'text-slate-500'}`}>Pages & Legal</button>
             <button onClick={() => setActiveTab('blog')} className={`px-4 py-2 rounded-md ${activeTab==='blog' ? 'bg-white shadow text-emerald-600' : 'text-slate-500'}`}>Blog Manager</button>
             <button onClick={() => setActiveTab('docs')} className={`px-4 py-2 rounded-md ${activeTab==='docs' ? 'bg-white shadow text-teal-600' : 'text-slate-500'}`}>Documentation</button>
             <button onClick={() => setActiveTab('preview')} className={`px-4 py-2 rounded-md transition-colors ${activeTab==='preview' ? 'bg-white shadow text-amber-600' : 'text-slate-500'}`}>Preview</button>
            </>
          )} 
           </div>
           
           {activeTab === 'settings' && (
               <button 
                onClick={() => saveSettingsMutation.mutate(settings)}
                disabled={saveSettingsMutation.isLoading}
                className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-bold shadow-lg shadow-emerald-500/20 flex items-center hover:bg-emerald-700 transition-transform active:scale-95"
               >
               <Save className="w-4 h-4 mr-2" /> {saveSettingsMutation.isLoading ? 'Saving...' : 'Save Changes'}
               </button>
           )}
        </div>
      </div>

      {/* --- TAB: GENERAL SETTINGS --- */}
      {activeTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-left-4">
           
           {/* Identity Card */}
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-5">
              <h3 className="font-bold text-slate-800 mb-2 flex items-center"><Globe className="w-5 h-5 mr-2 text-slate-500"/> Site Identity</h3>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Website Title</label>
                <input 
                    className="w-full p-2.5 border rounded-lg" 
                    value={settings.site_title} 
                    onChange={e => setSettings(prev => ({...prev, site_title: e.target.value}))} 
                />
              </div>

              <ImageUploader 
                 label="Site Logo" 
                 value={settings.site_logo} 
                 onChange={(url) => setSettings(prev => ({...prev, site_logo: url}))} 
              />
              
              <ImageUploader 
                 label="Site Favicon" 
                 value={settings.site_favicon} 
                 onChange={(url) => setSettings(prev => ({...prev, site_favicon: url}))} 
              />
           </div>

           {/* Hero Content Card */}
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-5">
              <h3 className="font-bold text-slate-800 mb-2 flex items-center"><Type className="w-5 h-5 mr-2 text-slate-500"/> Homepage Hero</h3>
              
              <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Headline</label>
                 <input 
                    className="w-full p-2.5 border rounded-lg font-bold" 
                    value={settings.hero_headline} 
                    onChange={e => setSettings(prev => ({...prev, hero_headline: e.target.value}))} 
                 />
              </div>
              
              <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Subtext</label>
                 <textarea 
                    className="w-full p-2.5 border rounded-lg h-32" 
                    value={settings.hero_subtext} 
                    onChange={e => setSettings(prev => ({...prev, hero_subtext: e.target.value}))} 
                 />
              </div>
           </div>
           
           {/* Matrix Config Card */}
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm lg:col-span-2">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center">
                 <Activity className="w-5 h-5 mr-2 text-green-600"/> Live Feed Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                 {['hybrid', 'live', 'demo'].map((mode) => (
                    <label key={mode} className={`border p-4 rounded-xl cursor-pointer transition-all ${settings.matrix_mode === mode ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-200'}`}>
                        <input 
                            type="radio" 
                            name="matrix" 
                            className="hidden" 
                            checked={settings.matrix_mode === mode} 
                            onChange={() => setSettings(prev => ({...prev, matrix_mode: mode}))} 
                        />
                        <div className="flex items-center mb-1">
                            <div className={`w-4 h-4 rounded-full border mr-2 flex items-center justify-center ${settings.matrix_mode === mode ? 'border-blue-600' : 'border-slate-400'}`}>
                                {settings.matrix_mode === mode && <div className="w-2 h-2 bg-blue-600 rounded-full"></div>}
                            </div>
                            <span className="font-bold text-slate-800 capitalize">{mode} Mode</span>
                        </div>
                    </label>
                 ))}
              </div>
           </div>

        </div>
      )}

      {/* --- TAB: FEATURES MANAGER --- */}
      {activeTab === 'features' && (
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl border border-purple-100 shadow-sm h-fit">
               <h3 className="font-bold text-slate-800 mb-4 flex items-center"><Layers className="w-5 h-5 mr-2 text-purple-600"/> {editingFeature ? 'Edit Feature' : 'Add Feature'}</h3>
               <div className="space-y-3">
                  <input placeholder="Title" className="w-full p-2 border rounded" value={featureForm.title} onChange={e => setFeatureForm({...featureForm, title: e.target.value})} />
                  <input placeholder="Subtitle" className="w-full p-2 border rounded uppercase text-xs" value={featureForm.subtitle} onChange={e => setFeatureForm({...featureForm, subtitle: e.target.value})} />
                  <textarea placeholder="Description..." className="w-full p-2 border rounded h-24" value={featureForm.description} onChange={e => setFeatureForm({...featureForm, description: e.target.value})} />
                  
                  {/* Image Upload for Feature */}
                  <ImageUploader 
                     label="Feature Image (Optional)" 
                     value={featureForm.image_url} 
                     onChange={(url) => setFeatureForm({...featureForm, image_url: url})} 
                  />

                  <div className="grid grid-cols-2 gap-2">
                     <input placeholder="Icon Name" className="p-2 border rounded" value={featureForm.icon_name} onChange={e => setFeatureForm({...featureForm, icon_name: e.target.value})} />
                     <input type="number" placeholder="Order" className="p-2 border rounded" value={featureForm.display_order} onChange={e => setFeatureForm({...featureForm, display_order: parseInt(e.target.value)})} />
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                     {editingFeature && <button onClick={() => { setEditingFeature(null); setFeatureForm({ title: '', subtitle: '', description: '', icon_name: 'Shield', image_url: '', display_order: 0 }); }} className="flex-1 py-2 border rounded text-slate-500">Cancel</button>}
                     <button onClick={() => saveFeatureMutation.mutate({...featureForm, id: editingFeature})} className="flex-1 bg-purple-600 text-white py-2 rounded font-bold hover:bg-purple-700">Save</button>
                  </div>
               </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
               {features.map((f) => (
                  <div key={f.id} className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center group hover:shadow-md transition-all">
                     <div className="flex items-center">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mr-4 text-slate-500 overflow-hidden">
                           {f.image_url ? <img src={f.image_url} alt="ico" className="w-full h-full object-cover" /> : <Star className="w-5 h-5" />}
                        </div>
                        <div>
                           <h4 className="font-bold text-slate-800">{f.title}</h4>
                           <p className="text-xs text-slate-500 uppercase">{f.subtitle}</p>
                        </div>
                     </div>
                     <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEditFeature(f)} className="p-2 bg-blue-50 text-teal-600 rounded hover:bg-blue-100"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => deleteFeatureMutation.mutate(f.id)} className="p-2 bg-red-50 text-red-600 rounded hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      )}

      {/* --- TAB: PRODUCT PAGE MANAGER --- */}
      {activeTab === 'products' && (
         <div className="space-y-8 animate-in fade-in">
             <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 flex items-start gap-4">
                <Box className="w-8 h-8 text-teal-600 mt-1" />
                <div>
                   <h3 className="font-bold text-teal-900 text-lg">Product Showcase Editor</h3>
                   <p className="text-teal-700 text-sm">Edit the 4 main sections of the "Product" page. Images are displayed in a Zig-Zag layout.</p>
                </div>
             </div>

             <div className="grid grid-cols-1 gap-8">
                 {productSections.map((section, idx) => (
                    <div key={section.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                       <h4 className="text-xs font-bold text-slate-400 uppercase mb-4 border-b pb-2 flex justify-between">
                          <span>Section {idx + 1}</span>
                          <span className="text-teal-500">{idx % 2 === 0 ? 'Image Right' : 'Image Left'}</span>
                       </h4>
                       <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div className="space-y-4">
                             <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Heading</label>
                                <input className="w-full p-2 border rounded font-bold" defaultValue={section.heading} onBlur={(e) => updateProductMutation.mutate({...section, heading: e.target.value})} />
                             </div>
                             <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sub-Heading</label>
                                <input className="w-full p-2 border rounded text-xs text-teal-600 font-bold uppercase" defaultValue={section.sub_heading} onBlur={(e) => updateProductMutation.mutate({...section, sub_heading: e.target.value})} />
                             </div>
                             <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Content</label>
                                <textarea className="w-full p-2 border rounded h-32 text-sm" defaultValue={section.content} onBlur={(e) => updateProductMutation.mutate({...section, content: e.target.value})} />
                             </div>

                             <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Key Points (Comma Separated)</label>
                                <textarea 
                                    className="w-full p-2 border rounded h-20 text-sm font-mono text-slate-600" 
                                    defaultValue={section.bullets ? section.bullets.join(', ') : ''} 
                                    placeholder="Point 1, Point 2, Point 3"
                                    onBlur={(e) => updateProductMutation.mutate({
                                        ...section, 
                                        bullets: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                                    })} 
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Separate points with a comma (,)</p>
                             </div>

                          </div>
                          <div>
                             <ImageUploader 
                                label="Section Image" 
                                value={section.image_url} 
                                onChange={(url) => updateProductMutation.mutate({...section, image_url: url})} 
                             />
                          </div>
                       </div>
                    </div>
                 ))}
             </div>
         </div>
      )}

      {/* --- TAB: PREVIEW (Uses settings directly) --- */}
      {activeTab === 'preview' && (
        <div className="bg-[#0b0f19] rounded-3xl overflow-hidden shadow-2xl border border-slate-800 relative min-h-screen">
            {/* Navbar */}
            <div className="h-20 border-b border-white/5 flex items-center px-8 justify-between sticky top-0 bg-[#0b0f19]/90 backdrop-blur z-50">
                {/* Logo */}
                <div className="flex items-center flex-shrink-0 cursor-pointer" onClick={() => setPreviewPage('home')} >
                   {settings.site_logo ? (
                       <img src={settings.site_logo} alt="Logo" className="h-10 mr-3 object-contain" />
                   ) : (
                       <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg mr-3 flex items-center justify-center font-bold text-white shadow-lg shadow-blue-500/20">A</div>
                   )}
                   <span className="text-white font-bold text-xl tracking-tight hidden sm:block">
                       {settings.site_title || 'AIRS'} <span className="text-teal-500">Security</span>
                   </span>
                </div>

                <div className="flex space-x-6 text-sm font-medium text-slate-300 overflow-x-auto scrollbar-hide px-4 mx-2">
                   <span 
                      onClick={() => setPreviewPage('home')}
                      className={`cursor-pointer transition-colors ${previewPage === 'home' ? 'text-white font-bold' : 'hover:text-white'}`}
                   >
                      Home
                   </span>
                   <span 
                      onClick={() => setPreviewPage('product')}
                      className={`cursor-pointer transition-colors ${previewPage === 'product' ? 'text-white font-bold' : 'hover:text-white'}`}
                   >
                      Features
                   </span>
                   <span 
                     onClick={() => setPreviewPage('pricing')} 
                     className={`cursor-pointer transition-colors ${previewPage === 'pricing' ? 'text-white font-bold' : 'hover:text-white'}`}
                  >
                     Pricing
                  </span>
                  <span onClick={() => setPreviewPage('blog')} className={`cursor-pointer ${previewPage === 'blog' ? 'text-white font-bold' : 'hover:text-white'}`}>Blog</span>
                </div>

            {/* Right: Live & Login (Visible now) */}
            <div className="flex items-center gap-5 flex-shrink-0">
                   {/* Live Indicator */}
                   <span className="flex items-center text-[10px] font-bold text-green-500 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20 tracking-wider">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse mr-2"></span> LIVE
                   </span>
                   
                   {/* Login Button (Circle Icon + Text) */}
                   <button className="flex items-center gap-3 pl-1 pr-5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all group cursor-pointer">
                       <div className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                           <User className="w-4 h-4 text-white" />
                       </div>
                       <span className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors">
                           <a href="https://account.airssecurity.com/login" className="text-slate-300 hover:text-white text-sm font-bold transition-colors">
                              Login
                           </a> 
                          {/* <a href="https://account.airssecurity.com/register" className="text-slate-300 hover:text-white text-sm font-bold transition-colors">
                              Get Started
                           </a>*/}
                       </span>
                   </button>
                </div>
        
            </div>

            {/* Hero */}
            {previewPage === 'home' && (
            <>
               <div className="animate-in fade-in duration-300 w-full border-b border-white/10 bg-[#020617] py-20">
                  <div className="max-w-7xl mx-auto px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6 text-left">
                     <h1 className="text-5xl md:text-6xl font-black text-white leading-[1.1] tracking-tight">
                        {settings.hero_headline === "The World's First Self-Healing AI Guardian." || !settings.hero_headline ? (
                        <>
                           The World's First <br />
                           <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                              Self-Healing AI
                           </span>{" "}
                           Guardian.
                        </>
                        ) : (
                        settings.hero_headline
                        )}
                     </h1>

                     <p className="text-lg text-slate-400 max-w-lg leading-relaxed">
                        {settings.hero_subtext ||
                        "Stop ransomware, reverse shells, and zero-day attacks in milliseconds. No cloud latency. No human intervention required."}
                     </p>

                     <p className="text-lg text-slate-400 max-w-lg leading-relaxed">
                        Designed for critical infrastructure, enterprise systems, and government environments.
                     </p>

                     <div className="flex gap-4 pt-4">
                        <button className="bg-blue-600 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-blue-500 shadow-lg shadow-blue-500/25 transition-all hover:scale-105">
                        Get Protected
                        </button>
                        <button className="px-8 py-3.5 rounded-xl font-bold text-white border border-white/10 hover:bg-white/5 transition-all">
                        View Demo
                        </button>
                     </div>
                  </div>

                  <div className="relative text-center">
                     <div className="pt-2 pb-2">
                        <div className="text-xs font-bold tracking-widest text-teal-500 uppercase bg-blue-500/10 px-3 py-3 rounded-full">
                        Real-time system telemetry · Read-only · Policy-controlled
                        </div>
                     </div>

                     <div className="p-1 rounded-2xl bg-gradient-to-b from-slate-700 to-slate-900 shadow-2xl">
                        <div className="bg-[#0a0f1c] rounded-xl overflow-hidden">
                        <MatrixFeed />
                        </div>
                     </div>

                     <div className="pt-2">
                        <div className="bg-white/5 backdrop-blur-md px-3 py-3 rounded-full inline-flex items-center border border-white/10">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-3"></span>
                        <span className="text-xs font-mono text-red-300 tracking-wider">
                           LATEST:{" "}
                           <span className="text-white font-bold">
                              {publicStats?.recent_threats?.[0] || "Scanning..."}
                           </span>
                        </span>
                        </div>
                     </div>
                  </div>
                  </div>
               </div>

               {/* Features */}
               <div className="max-w-6xl mx-auto px-6 py-24">
                  <FeatureSection features={features} />
               </div>

               {/* Live Stats */}
               <div className="border-t border-white/5 bg-[#020617] relative overflow-hidden">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />

                  <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 relative">
                     <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
                        <div>
                        <span className="inline-flex rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-teal-400">
                           AIRS Live Metrics
                        </span>
                        <h2 className="mt-5 text-3xl md:text-5xl font-black text-white tracking-tight">
                           Protection intelligence in real time.
                        </h2>
                        </div>

                        <div className="inline-flex items-center gap-3 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-emerald-400 w-fit">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                        Operational
                        </div>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {/* Card 1: Analyzed */}
                        <div className="group relative overflow-hidden rounded-[2rem] border border-blue-500/20 bg-slate-950/70 p-6 md:p-8 min-h-[260px] shadow-2xl hover:-translate-y-1 hover:border-blue-400/40 hover:bg-white/[0.045] transition-all duration-300">
                        <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl group-hover:bg-blue-500/20 transition-all" />

                        <div className="relative flex items-center justify-between border-b border-white/10 pb-6">
                           <div className="h-14 w-14 rounded-2xl border border-blue-500/20 bg-blue-500/10 text-teal-400 flex items-center justify-center shadow-lg shadow-blue-500/10 group-hover:scale-105 transition-transform">
                              <Cpu className="w-7 h-7" />
                           </div>

                           <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                              <span className="h-2 w-2 rounded-full bg-teal-400 animate-pulse" />
                              01
                           </div>
                        </div>

                        <div className="relative mt-7">
                           <h3 className="font-black tracking-tight leading-[0.95] text-teal-400 text-[clamp(1.7rem,3.2vw,3rem)] break-all max-w-full">
                              {formatLargeNumber(publicStats?.total_analyzed) || '0'}
                           </h3>

                           <p className="mt-5 text-sm font-black text-white uppercase tracking-[0.16em]">
                              Events Analyzed
                           </p>

                           <p className="mt-3 text-sm text-slate-400 leading-relaxed min-h-[44px]">
                              Continuously processed system telemetry and behavioral signals.
                           </p>

                           <div className="mt-7 flex items-center gap-3">
                              <div className="h-1 w-14 rounded-full bg-teal-400" />
                              <div className="h-1 w-5 rounded-full bg-slate-700" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                              Live Feed
                              </span>
                           </div>
                        </div>
                        </div>

                        {/* Card 2: Threats */}
                        <div className="group relative overflow-hidden rounded-[2rem] border border-red-500/20 bg-slate-950/70 p-6 md:p-8 min-h-[260px] shadow-2xl hover:-translate-y-1 hover:border-red-400/40 hover:bg-white/[0.045] transition-all duration-300">
                        <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-red-500/10 blur-3xl group-hover:bg-red-500/20 transition-all" />

                        <div className="relative flex items-center justify-between border-b border-white/10 pb-6">
                           <div className="h-14 w-14 rounded-2xl border border-red-500/20 bg-red-500/10 text-red-400 flex items-center justify-center shadow-lg shadow-red-500/10 group-hover:scale-105 transition-transform">
                              <ShieldAlert className="w-7 h-7" />
                           </div>

                           <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                              <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse" />
                              02
                           </div>
                        </div>

                        <div className="relative mt-7">
                           <h3 className="font-black tracking-tight leading-[0.95] text-red-400 text-[clamp(1.7rem,3.2vw,3rem)] break-all max-w-full">
                              {publicStats?.threats_blocked?.toLocaleString() || '0'}
                           </h3>

                           <p className="mt-5 text-sm font-black text-white uppercase tracking-[0.16em]">
                              Threats Neutralized
                           </p>

                           <p className="mt-3 text-sm text-slate-400 leading-relaxed min-h-[44px]">
                              Policy-approved security responses applied across protected systems.
                           </p>

                           <div className="mt-7 flex items-center gap-3">
                              <div className="h-1 w-14 rounded-full bg-red-400" />
                              <div className="h-1 w-5 rounded-full bg-slate-700" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                              Protected
                              </span>
                           </div>
                        </div>
                        </div>

                        {/* Card 3: Agents */}
                        <div className="group relative overflow-hidden rounded-[2rem] border border-green-500/20 bg-slate-950/70 p-6 md:p-8 min-h-[260px] shadow-2xl hover:-translate-y-1 hover:border-green-400/40 hover:bg-white/[0.045] transition-all duration-300">
                        <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-green-500/10 blur-3xl group-hover:bg-green-500/20 transition-all" />

                        <div className="relative flex items-center justify-between border-b border-white/10 pb-6">
                           <div className="h-14 w-14 rounded-2xl border border-green-500/20 bg-green-500/10 text-green-400 flex items-center justify-center shadow-lg shadow-green-500/10 group-hover:scale-105 transition-transform">
                              <Activity className="w-7 h-7" />
                           </div>

                           <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                              <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                              03
                           </div>
                        </div>

                        <div className="relative mt-7">
                           <h3 className="font-black tracking-tight leading-[0.95] text-green-400 text-[clamp(1.7rem,3.2vw,3rem)] break-all max-w-full">
                              {publicStats?.active_sentinels?.toLocaleString() || '0'}
                           </h3>

                           <p className="mt-5 text-sm font-black text-white uppercase tracking-[0.16em]">
                              Active Sentinels
                           </p>

                           <p className="mt-3 text-sm text-slate-400 leading-relaxed min-h-[44px]">
                              Live agents connected to the AIRS autonomous protection layer.
                           </p>

                           <div className="mt-7 flex items-center gap-3">
                              <div className="h-1 w-14 rounded-full bg-green-400" />
                              <div className="h-1 w-5 rounded-full bg-slate-700" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                              Online
                              </span>
                           </div>
                        </div>
                        </div>
                     </div>
                  </div>
                  </div>
               <CookieConsent text={settings.cookie_consent_text} />
            </>
            )}

            {/* PRODUCT SHOWCASE */}
            {previewPage === 'product' && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-500">
                    <div className="py-20 bg-[#020617] text-center">
                        <h1 className="text-4xl font-black text-white mb-4">Our Technology</h1>
                        <p className="text-slate-400">Advanced defense mechanisms engineered for zero-trust environments.</p>
                    </div>
                    {/* Uses the Zig-Zag Product Component */}
                    <ProductShowcase sections={productSections} />
                </div>
            )}

            {previewPage === 'pricing' && (
               <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 min-h-screen bg-[#0b0f19]">
                  <PricingSection packages={publicPackages} />
               </div>
            )}

            {previewPage === 'download' && (
                <div className="animate-in fade-in bg-[#0b0f19] min-h-screen">
                    <PublicDownloadPage />
                </div>
            )}

            {previewPage === 'docs' && (
                <div className="animate-in fade-in bg-[#0b0f19] min-h-screen pt-20">
                    <PublicDocsPage />
                </div>
            )}

            {previewPage === 'changelog' && (
                <div className="animate-in fade-in bg-[#0b0f19] min-h-screen">
                    <ChangelogPage />
                </div>
            )}

            {previewPage === 'contact' && (
                <div className="animate-in fade-in bg-[#0b0f19] min-h-screen">
                    <ContactPage />
                </div>
            )}

            {previewPage === 'blog' && (
                <div className="animate-in fade-in bg-slate-50 min-h-screen">
                    <PublicBlogPage 
                        isPreview={true} 
                        // Trick: Set state to string "blog:slug" to handle navigation
                        onRead={(slug) => setPreviewPage(`blog:${slug}`)} 
                    />
                </div>
            )}

            {previewPage.startsWith('blog:') && (
                <div className="animate-in fade-in bg-white min-h-screen">
                    <BlogPost 
                        isPreview={true} 
                        // Extract slug from string "blog:my-slug"
                        slug={previewPage.split(':')[1]} 
                        onBack={() => setPreviewPage('blog')} 
                    />
                </div>
            )}

            {['about', 'privacy', 'terms', 'cookie-policy', 'gdpr', 'refund', 'partners', 'careers'].includes(previewPage) && (
                <div className="animate-in fade-in bg-white min-h-screen">
                    {/* Reuse your CmsPageView to fetch and display content */}
                    <CmsPageView slug={previewPage} />
                </div>
            )}

            {!['home', 'product', 'pricing', 'blog', 'download', 'docs', 'changelog', 'contact'].includes(previewPage) && !previewPage.startsWith('blog:') && (
                <div className="animate-in fade-in bg-white min-h-screen">
                    <CmsPageView slug={previewPage} />
                </div>
            )}
            <div className="mt-12 mb-12 flex justify-center px-4">
               <div className="relative overflow-hidden max-w-6xl rounded-[1.8rem] border border-white/10 bg-slate-950/70 backdrop-blur-xl shadow-2xl">
                  
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-400/40 to-transparent"></div>

                  <div className="absolute -left-20 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-teal-500/10 blur-3xl"></div>
                  <div className="absolute -right-20 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl"></div>

                  <div className="relative flex flex-col md:flex-row items-center gap-5 px-6 md:px-8 py-5 md:py-6">
                     
                     <div className="shrink-0">
                     <div className="h-14 w-14 rounded-2xl border border-teal-500/20 bg-teal-500/10 flex items-center justify-center shadow-lg shadow-teal-500/10">
                        <ShieldCheck className="w-7 h-7 text-teal-400" />
                     </div>
                     </div>

                     <div className="flex-1 text-center md:text-left">
                     <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-3">
                        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-400">
                           <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
                           Privacy Focused
                        </span>

                        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-600">
                           AIRS Security Platform
                        </span>
                     </div>

                     <p className="text-sm md:text-[15px] leading-relaxed text-slate-300">
                        AIRS Security is an autonomous security monitoring and response platform.
                        It does not collect personal user content beyond what is required for
                        system protection, threat analysis, integrity validation, and auditability.
                     </p>
                     </div>
                  </div>
               </div>
               </div>
            
            
            <SiteFooter settings={settings} onNavigate={setPreviewPage} />
        </div>
      )}
      {/* --- TAB: PAGES & LEGAL --- */}
      {activeTab === 'pages' && (
      <div className="animate-in fade-in slide-in-from-left-4">
         <LegalPagesManager />
      </div>
      )}

      {/* BLOG TAB CONTENT (Place it here) */}
      {activeTab === 'blog' && (
         <div className="animate-in fade-in slide-in-from-left-4">
             {/* Ensure BlogManager is imported at the top */}
             <BlogManager />
         </div>
      )}

      {activeTab === 'docs' && <div className="animate-in fade-in slide-in-from-left-4"><DocsManager /></div>}

    </div>
  );
};

export default CmsPage;